/**
 * How each ISFR check's evidence reads on the review view.
 *
 * Every check records structured evidence: a deterministic check's figures (the
 * calculator total, the funded days, the ceiling) and a reasoning check's full
 * structured response as the agent's review criteria declare it (each
 * design-win criterion with its own score and assessment, the questionable cost
 * items with their amounts). This module turns that evidence into labelled
 * values, paragraphs and tables. Pure, so every rule is unit-tested.
 *
 * Specs are keyed by the agent's check id. The id is an internal key here and is
 * never shown to a reviewer.
 *
 * Nothing is dropped silently. A key a spec does not name is still shown, under
 * a label made from the key. Left out: keys a spec lists as `internal` (engine
 * switches with no meaning for a review), the bookkeeping every check shares
 * (the raw result, which the row already states, and the cited quotes, which
 * render on their own), a yes/no a spec marks `onlyIfTrue` while it is no, and
 * a paragraph that only repeats the check's one-line details.
 */

// Every check's bookkeeping: the raw result (the row already states it), the
// cited quotes (they render on their own), the routing code behind a deferral
// (the `reason` beside it says why in words) and the partner-clause codes (the
// issues list beside them says the same in words).
const SHARED_INTERNAL = new Set(['result', 'evidence_quotes', 'defer_cause', 'field_issue_codes']);

const money = (n) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const isNumber = (v) => typeof v === 'number' && Number.isFinite(v);

/** "monthly_estimate" -> "Monthly estimate". */
export const humanize = (key) => {
  const words = String(key).replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const formatDate = (value) => {
  const date = new Date(typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? `${value}T00:00:00Z` : value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
};

/**
 * One end of the plan as the Plan Dates check read it: each date with every reading
 * of it, or the passage it quoted when no date was read.
 */
export const planSideText = (side) => {
  if (!side || !Array.isArray(side.dates) || side.dates.length === 0) {
    return side && side.passage ? `"${side.passage}"` : 'not found';
  }
  return side.dates.map((d) => {
    const readings = (d.readings || []).map((r) => formatDate(r.first));
    return readings.length > 1 ? `"${d.text}" (read as ${readings.join(' or ')})` : `"${d.text}"`;
  }).join(', ');
};

/**
 * One scalar value as text, by format. A value that does not fit its format is
 * shown as stored rather than coerced.
 */
export function formatValue(value, format, valueLabels) {
  if (valueLabels && Object.prototype.hasOwnProperty.call(valueLabels, String(value))) {
    return valueLabels[String(value)];
  }
  if (typeof value === 'boolean') {
    if (format === 'present') return value ? 'Present' : 'Missing';
    return value ? 'Yes' : 'No';
  }
  if (value === null || value === undefined || value === '') return '';
  if (isNumber(value)) {
    switch (format) {
      case 'money': return money(value);
      case 'score': return `${value} / 100`;
      case 'percent': return `${Number(value.toFixed(1))}%`;
      case 'days': return `${value.toLocaleString('en-US')} ${value === 1 ? 'day' : 'days'}`;
      case 'months': return `${value} ${value === 1 ? 'month' : 'months'}`;
      case 'chars': return `${value.toLocaleString('en-US')} characters`;
      case 'text': return String(value);
      default: return value.toLocaleString('en-US');
    }
  }
  if (format === 'date') return formatDate(value);
  if (Array.isArray(value)) return value.map(String).join(', ');
  return String(value);
}

const f = (key, label, format = 'text', extra = {}) => ({ key, label, format, ...extra });

const ASSESSMENT = f('assessment', 'Assessment', 'paragraph');
// Which submission text a check reviewed when no readable plan was there to read.
const REVIEW_BASIS = f('review_basis', 'Reviewed from', 'text', {
  valueLabels: { business_description: 'The business description', innovation_plan: 'The innovation plan' },
});
// What the cost-materiality rule did with a check's service lists: the services named
// but not priced (the details open with them), the small priced ones that count
// because together they reach the cutoff, and the ones set aside as immaterial.
const MATERIALITY = [
  f('unpriced_services', 'Named but not priced in the calculator', 'list', { pick: 'service', inDetails: true }),
  f('material_together', 'Counted together (each priced under the monthly cutoff)', 'table', {
    columns: [['names', 'Services'], ['monthly_total', 'Monthly total', 'money']],
  }),
  f('immaterial_services', 'Set aside as immaterial', 'table', {
    columns: [['service', 'Service'], ['why', 'Why', undefined, {
      exempt: 'Costs nothing', under_cutoff: 'Priced under the monthly cutoff', near_free: 'Near-free service',
    }], ['monthly', 'Monthly', 'money']],
  }),
];
const OVERALL_ASSESSMENT = f('overall_assessment', 'Assessment', 'paragraph');
// inDetails: the agent writes the list into the check's details as well.
const ISSUES = f('issues', 'Issues found', 'list', { inDetails: true });
const SCA = f('is_sca', 'Strategic Collaboration Agreement', 'yesno');

const DOCUMENT_LABELS = {
  calculator: 'Pricing calculator', innovation_plan: 'Innovation plan', architecture_diagram: 'Architecture diagram',
};

/**
 * The name to show for a stored file name, or a list of them: the agent's plain copy
 * (`<field>_display`) when it saved one, otherwise the value as stored.
 */
export const shownName = (owner, field) => {
  if (!owner || typeof owner !== 'object') return undefined;
  const plain = owner[`${field}_display`];
  return plain !== undefined && plain !== null ? plain : owner[field];
};

const joinAnd = (items) => (items.length < 2 ? items.join('')
  : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`);

// What the documents check's input rows leave out, as short lines: a usable file set
// aside for another of its type, calculator links the review did not open, and files
// the request does not need. A set-aside file the row's own reason names (a calculator
// file in `set_aside`) is not named again.
const documentNotes = (evidence) => {
  const status = evidence.input_status && typeof evidence.input_status === 'object' ? evidence.input_status : {};
  const notes = [];
  Object.entries(status).forEach(([slot, entry]) => {
    if (!entry || typeof entry !== 'object') return;
    const inReason = new Set((Array.isArray(entry.set_aside) ? entry.set_aside : []).map((e) => e && e.name));
    const stored = Array.isArray(entry.set_aside_files) ? entry.set_aside_files : [];
    const shown = shownName(entry, 'set_aside_files');
    const setAside = Array.isArray(shown) ? shown.filter((_, i) => !inReason.has(stored[i])) : shown;
    const reviewed = shownName(entry, 'reviewed_file');
    if (Array.isArray(setAside) && setAside.length > 0 && reviewed) {
      const count = isNumber(entry.attached_count) ? entry.attached_count : setAside.length + 1;
      notes.push(`${DOCUMENT_LABELS[slot] || humanize(slot)}: ${count} files attached; the review read '${reviewed}' `
        + `and set aside ${joinAnd(setAside.map((n) => `'${n}'`))}.`);
    }
  });
  const calculator = status.calculator && typeof status.calculator === 'object' ? status.calculator : {};
  if (isNumber(calculator.link_count) && calculator.link_count > 1 && calculator.reviewed_link) {
    const others = Array.isArray(calculator.links_set_aside) ? calculator.links_set_aside : [];
    notes.push(`Pricing calculator: ${calculator.link_count} links shared; the review took the first, '${calculator.reviewed_link}'`
      + `${others.length > 0 ? `, and did not open ${joinAnd(others.map((l) => `'${l}'`))}` : ''}.`);
  }
  if (isNumber(evidence.extras_count) && evidence.extras_count > 0) {
    notes.push(`${evidence.extras_count} other ${evidence.extras_count === 1 ? 'file' : 'files'} attached that the request does not need.`);
  }
  return notes;
};

/** What a check found an input to be. */
export const INPUT_STATE = {
  OK: 'ok',
  ISSUE: 'issue',
  MISSING: 'missing',
  NOT_REQUIRED: 'not_required',
};

/** What each of the documents check's input statuses means, and a short form of it. */
export const INPUT_STATUS = {
  ok: { kind: INPUT_STATE.OK, short: 'Readable' },
  missing: { kind: INPUT_STATE.MISSING, short: 'Not attached' },
  wrong_file_type: { kind: INPUT_STATE.ISSUE, short: 'A type the review cannot read' },
  could_not_open: { kind: INPUT_STATE.ISSUE, short: 'Could not be opened' },
  unreadable: { kind: INPUT_STATE.ISSUE, short: 'Nothing readable in it' },
  not_validatable: { kind: INPUT_STATE.ISSUE, short: 'Not a structured estimate' },
  not_required: { kind: INPUT_STATE.NOT_REQUIRED, short: 'Not required for this request' },
};

/** An input status as a state kind; a status this view does not know is an issue. */
export const inputKind = (status) => (INPUT_STATUS[status] ? INPUT_STATUS[status].kind : INPUT_STATE.ISSUE);

/**
 * Whether a missing input blocks the request. A missing separate diagram is a
 * requirement the documents check records as not enforced yet, so it never blocks.
 */
export const inputEnforced = (slot, documentsEvidence) => !(slot === 'architecture_diagram'
  && Boolean(documentsEvidence) && documentsEvidence.diagram_requirement_gated === true);

/**
 * The status indicator an input's state shows as, the same everywhere it appears:
 * readable is a success, not required is stopped, an issue is a warning, and a
 * missing input is an error, or a warning while its requirement is not enforced.
 */
export const inputIndicator = (kind, enforced = true) => {
  switch (kind) {
    case INPUT_STATE.OK: return 'success';
    case INPUT_STATE.NOT_REQUIRED: return 'stopped';
    case INPUT_STATE.MISSING: return enforced ? 'error' : 'warning';
    default: return 'warning';
  }
};

/** How a design-win criterion's supporting passage was found, by the check's plan_quote_status. */
const PLAN_QUOTE_STATUS = {
  located: 'Found in the plan',
  blank: 'Not described in the plan',
  description_only: 'Only in the business description, not the plan',
  description_unconfirmed: 'In the business description; whether the plan shows it is for you to verify',
  too_short: 'Passage too short to count; verify',
  unlocated: 'Passage not found in the plan or the description; verify',
  absent: 'No passage returned; verify',
};

/** A result word as a reviewer reads it in a sentence. */
const RESULT_WORD = { PASS: 'pass', FLAG: 'flag', FAIL: 'fail' };
const RESULT_SHOWN = {
  PASS: 'Passed', FAIL: 'Failed', FLAG: 'Flagged', DEFERRED: 'Not completed', UNVERIFIED: 'Not completed', PARTIAL: 'Not completed',
};

const DESIGN_WIN_CRITERIA = [
  { key: 'novelty', label: 'Novelty', flags: { is_new: 'New solution', is_rearchitecture: 'Re-architecture', is_enhancement: 'Enhancement of an existing product' } },
  { key: 'aws_integration', label: 'AWS integration', lists: { core_aws_services: 'Core AWS services' } },
  { key: 'broad_availability', label: 'Broad availability', flags: { is_saas: 'Offered as SaaS', is_customer_specific: 'Built for one customer' } },
  { key: 'partner_ip', label: 'Partner IP', flags: { has_proprietary_ip: 'Partner-owned IP' } },
  { key: 'production_intent', label: 'Production intent', flags: { has_timeline_to_ga: 'Timeline to general availability' } },
];

/** Display specs, keyed by the agent's check id. */
export const EVIDENCE_SPECS = {
  '1.4': {
    // The input rows say each input's state and why; the check's details say the same
    // at length, so where the rows render the page shows them, and the download keeps
    // the details. A record without the rows keeps its details on the page.
    detailsOnPage: false,
    detailsReplacedBy: 'input_status',
    fields: [
      f('input_status', 'Submitted inputs', 'inputs', { labels: DOCUMENT_LABELS }),
      f('input_status', 'Also on this request', 'documentNotes'),
      f('calc_source_used', 'Calculator source used'),
      f('calc_source_conflict', 'Calculator source conflict'),
      f('calc_dual_source', 'Calculator sent both as a file and as a link', 'yesno', { onlyIfTrue: true }),
    ],
    // The rows and the lines above carry these; the download keeps them.
    internal: ['enforcement_enabled', 'diagram_requirement_gated', 'missing', 'unusable_inputs', 'conflicts',
      'found_counts', 'extras_count', 'issues', 'fail_reason'],
  },
  '1.5': {
    fields: [
      f('checks', 'Submission fields', 'fields', {
        labels: {
          project_duration_days: 'Project length',
          date_range_valid: 'Start and end dates valid',
          activity_name: 'Activity name',
          follows_convention: "Activity name follows 'Partner | Solution | Year'",
          account_id: 'AWS account ID',
          business_description_length: 'Business description length',
          budget_year: 'Budget year',
        },
        formats: {
          project_duration_days: 'days', date_range_valid: 'yesno', follows_convention: 'yesno',
          business_description_length: 'chars', budget_year: 'text', account_id: 'text',
        },
      }),
      ISSUES,
    ],
  },
  '1.6': {
    fields: [f('amount', 'Requested amount', 'money'), f('cap', 'Cap at this tier', 'money'), SCA],
    internal: ['tier'],
  },
  '1.7': {
    fields: [f('account_id', 'AWS account ID'), f('valid_format', 'Valid 12-digit format', 'yesno')],
  },
  '1.9': {
    fields: [f('start_date', 'Planned start', 'date'), f('flags', 'Timing flags', 'list', { inDetails: true })],
  },
  '1.13': {
    // The line names the approvals needed and, when any are, the amount that needs
    // them; nothing more to show beneath it.
    line: (evidence, details) => (
      Array.isArray(evidence.issues) && evidence.issues.length > 0 && typeof evidence.amount === 'number'
        ? `${details} (requested ${formatValue(evidence.amount, 'money')}).` : details),
    fields: [],
    internal: ['tier', 'amount', 'issues'],
  },
  '1.16': {
    fields: [
      f('triggered_terms', 'Distributor or on-behalf-of terms found', 'list'),
      f('on_behalf_of_downstream', 'Submitted on behalf of a downstream seller', 'yesno'),
      f('downstream_entity', 'Downstream seller'),
      f('reasoning', 'Reasoning', 'paragraph'),
      ASSESSMENT,
    ],
  },
  '1.19': {
    fields: [f('matched_markers', 'Special-program markers found', 'list'), f('activity_name', 'Activity name')],
  },
  '2.1': {
    fields: [
      f('monthly_estimate', 'Calculator monthly total', 'money'),
      f('window_months', 'Funding window', 'months'),
      f('project_days', 'Project length', 'days'),
      f('funded_days', 'Days funded', 'days'),
      f('daily_rate', 'Daily rate', 'money'),
      f('max_allowed', 'Maximum allowed (daily rate × days funded)', 'money'),
      f('tolerance', 'Tolerance', 'money'),
      f('allowed_ceiling', 'Allowed ceiling, with tolerance', 'money'),
      f('requested_amount', 'Requested amount', 'money'),
      f('difference', 'Requested over the ceiling by', 'money'),
      SCA,
    ],
  },
  '2.3': {
    fields: [
      f('alignment_score', 'Alignment score', 'score'),
      f('matched', 'In both the calculator and the description', 'list'),
      f('calculator_only', 'In the calculator, not the description', 'list'),
      f('description_only', 'In the description, not the calculator', 'list'),
      f('calculator_services', 'All calculator services', 'list'),
      f('description_services', 'All services the description names', 'list'),
      ...MATERIALITY,
      ASSESSMENT,
    ],
  },
  '2.4': {
    fields: [
      f('error', 'Error', 'paragraph'),
      f('failure', 'Failure', 'text', { valueLabels: { read_timeout: 'The model call timed out' } }),
      f('questionable_items', 'Questionable items', 'table', { columns: [['service', 'Service'], ['monthly', 'Monthly', 'money'], ['assessment', 'Assessment']] }),
      f('reasonable_items', 'Reasonable items', 'table', { columns: [['service', 'Service'], ['monthly', 'Monthly', 'money'], ['assessment', 'Assessment']] }),
      f('suggested_monthly_range', 'Suggested monthly range', 'range'),
      OVERALL_ASSESSMENT,
    ],
  },
  '2.5': {
    fields: [
      f('stated_purpose', 'Stated purpose', 'paragraph'),
      f('core_percentage', 'Share of cost tied to the purpose', 'percent'),
      f('core_monthly', 'Core monthly cost', 'money'),
      f('non_core_monthly', 'Non-core monthly cost', 'money'),
      f('score_result_contradiction', 'Score and verdict disagree', 'contradiction'),
      f('core_services', 'Core services', 'table', { columns: [['service', 'Service'], ['monthly', 'Monthly', 'money'], ['reason', 'Why it is core']] }),
      f('non_core_services', 'Non-core services', 'table', { columns: [['service', 'Service'], ['monthly', 'Monthly', 'money'], ['reason', 'Why it is not core']] }),
      ASSESSMENT,
    ],
  },
  '2.6': {
    fields: [
      f('total_items', 'Line items reviewed'),
      f('flagged_items', 'Flagged as production-scale'),
      f('dev_appropriate_items', 'Sized for development'),
      f('flags', 'Sizing flags', 'list'),
    ],
  },
  '2.8': { fields: [f('regions', 'Regions', 'list', { inDetails: true })], internal: ['count'] },
  '2.9': { fields: [f('non_on_demand_items', 'Items not on On-Demand pricing', 'list')], internal: ['count'] },
  '2.10': {
    fields: [
      f('transfer_items', 'Data transfer items', 'table', { columns: [['service', 'Service'], ['monthly', 'Monthly', 'money']] }),
      f('total_outbound_cost', 'Outbound transfer cost per month', 'money'),
      f('pct_of_total', 'Share of the monthly total', 'percent'),
    ],
  },
  '2.11': {
    fields: [
      f('format', 'Format', 'text', {
        valueLabels: { pricing_calculator: 'AWS Pricing Calculator export', partner_authored_excel: 'A cost file not in AWS Pricing Calculator format' },
      }),
      f('line_item_count', 'Line items'),
      f('total_monthly', 'Monthly total', 'money'),
      f('trust_reason', 'Why it could not be validated', 'text', {
        valueLabels: {
          unstructured_cost: 'It has no per-service line items',
          costs_unread: 'One or more of its monthly costs could not be read',
          non_reconciling: 'Its line items do not add up to its stated total',
        },
      }),
      f('stated_total_monthly', 'Stated monthly total', 'money'),
      f('recovered_total_monthly', 'Monthly total from its line items', 'money'),
      f('parse_record', 'How it was read', 'fields', {
        labels: {
          origin: 'Read from',
          lines: 'Line items',
          cost: 'With a cost',
          region: 'With a region',
          config: 'With a configuration',
          stated_total: 'States a total',
          subtotal_rows: 'Subtotal rows',
          rows_not_read: 'Rows not read',
          costs_unattributed: 'Costs not tied to a line item',
        },
        formats: { stated_total: 'yesno', origin: 'text' },
      }),
    ],
  },
  '3.1': {
    fields: [
      f('fields_present', 'Template fields', 'fields', {
        labels: {
          activity_name: 'Activity name',
          business_description: 'Business description',
          aws_calculator_url: 'Pricing calculator link',
          sandbox_design_win_activity: 'Design-win activity',
        },
        defaultFormat: 'present',
      }),
      ISSUES,
    ],
    internal: ['tier'],
  },
  '3.2': {
    fields: [
      f('activity_name', 'Activity name'),
      f('description_length', 'Description length', 'chars'),
      f('budget_year', 'Budget year', 'text'),
      ISSUES,
    ],
  },
  '3.3-T1': { fields: [f('keywords_found', 'Ineligible keywords found', 'list'), REVIEW_BASIS], internal: ['count'] },
  '3.3-T2': {
    fields: [
      f('ineligible_activities_found', 'Activities that may not be eligible', 'table', { columns: [['activity', 'Activity'], ['reason', 'Why'], ['severity', 'Severity']] }),
      f('eligible_activities_found', 'Eligible activities', 'table', { columns: [['activity', 'Activity'], ['reason', 'Why']] }),
      f('is_customer_specific', 'Built for one customer', 'yesno'),
      f('customer_name', 'Customer named'),
      f('is_internal_only', 'Internal-only work', 'yesno'),
      f('is_service_testing', 'Testing an AWS service', 'yesno'),
      REVIEW_BASIS,
      OVERALL_ASSESSMENT,
    ],
  },
  '3.4': {
    fields: [
      f('plan_quotes_unlocated_note', 'To verify', 'paragraph'),
      f('criteria', 'Design-win criteria', 'criteria', { criteria: DESIGN_WIN_CRITERIA }),
      f('overall_confidence', 'Overall confidence', 'score'),
      f('scored_result', 'Result the score gives', 'text', { valueLabels: { PASS: 'Pass', FLAG: 'Flag', FAIL: 'Fail' } }),
      f('score_result_contradiction', 'Score and verdict disagree', 'contradiction'),
      f('plan_details_missing', 'Not described in the plan', 'criteriaList'),
      f('plan_details_in_description', 'Only in the business description', 'criteriaList'),
      f('plan_quotes_unlocated', 'Passages to verify', 'criteriaList'),
      // The review from the business description, when no readable plan was there.
      f('purpose', 'Purpose', 'paragraph'),
      f('use_case', 'Use case', 'text', {
        valueLabels: { new_solution: 'New solution', enhancement: 'Enhancement of an existing product', none: 'None', unclear: 'Unclear' },
      }),
      f('customer_need', 'Customer need', 'paragraph'),
      f('end_result', 'End result', 'paragraph'),
      f('single_end_customer', 'Built for a single end customer', 'yesno'),
      REVIEW_BASIS,
      OVERALL_ASSESSMENT,
    ],
    // Shown per criterion in the criteria block; the attempts disagreeing is in the details.
    internal: ['plan_quote_status', 'plan_quote_verified', 'plan_quote_recheck', 'attempt_verdicts', 'reviewer_settles'],
  },
  '3.7': {
    fields: [f('optional_attachment_parts', 'Checks run on what the partner attached', 'parts')],
  },
  '3.8': {
    fields: [
      f('date_comparison', 'Dates compared', 'planDates'),
      ASSESSMENT,
      f('plan_files_unread', 'Plan files not read', 'list', { pick: ['display_name', 'name'] }),
    ],
    // The same dates, flat; date_comparison carries them and the details say the outcome.
    // The set-aside plans that hold dates are named in the details by their plain names.
    internal: ['request_start', 'request_end', 'plan_dates_found', 'date_source', 'submitted', 'submission_date_source',
      'plan_start_readings', 'plan_end_readings', 'plan_readings_align', 'window_holds', 'ends_after',
      'plan_readings_set_aside', 'plan_readings_one_date_set_aside', 'plan_start', 'plan_end', 'start_passage', 'end_passage',
      'plan_files_with_dates'],
  },
  '3.6': {
    fields: [
      f('diagram_components', 'Components in the diagram', 'list'),
      f('services_in_diagram_not_priced', 'In the diagram, not priced', 'list'),
      f('services_priced_not_in_diagram', 'Priced, not in the diagram', 'list'),
      f('missing_or_inconsistent', 'Missing or inconsistent', 'paragraph'),
      ...MATERIALITY,
      OVERALL_ASSESSMENT,
    ],
  },
  '5.10': {
    fields: [
      f('contradictions', 'Contradictions found', 'list', { inDetails: true }),
      f('checks_considered', 'Checks compared', 'checkRefs'),
    ],
  },
};

/** Columns for a table of objects with no column spec: every key seen, in first-seen order. */
const inferColumns = (rows) => {
  const keys = [];
  rows.forEach((row) => Object.keys(row || {}).forEach((k) => { if (!keys.includes(k)) keys.push(k); }));
  return keys.map((k) => [k, humanize(k), /monthly|cost|amount/.test(k) ? 'money' : undefined]);
};

const asTable = (label, rows, columns) => ({
  kind: 'table',
  label,
  columns: (columns || inferColumns(rows)).map(([key, title, format]) => ({ key, title, format })),
  rows: rows.map((row) => Object.fromEntries((columns || inferColumns(rows)).map(([key, , format, labels]) => [
    key, formatValue(row ? row[key] : undefined, format, labels),
  ]))),
});

const describeList = (label, value, nameOf, valueLabels) => {
  if (value.length === 0) return { kind: 'value', label, text: 'None' };
  if (value.every((v) => v !== null && typeof v === 'object' && !Array.isArray(v))) {
    return asTable(label, value);
  }
  return {
    kind: 'list',
    label,
    items: value.map((v) => (nameOf ? nameOf(String(v)) : formatValue(v, undefined, valueLabels))),
  };
};

const CRITERION_LABELS = Object.fromEntries(DESIGN_WIN_CRITERIA.map((c) => [c.key, c.label]));

/** One evidence entry, by its spec, as display items. */
function describeField(spec, value, nameOf, evidence = {}) {
  const { label, format } = spec;
  switch (format) {
    case 'paragraph':
      return [{ kind: 'paragraph', label, text: String(value) }];
    case 'list':
      return Array.isArray(value)
        ? [describeList(label, value, undefined, spec.valueLabels)]
        : [{ kind: 'value', label, text: formatValue(value) }];
    case 'criteriaList':
      return Array.isArray(value) ? [describeList(label, value, undefined, CRITERION_LABELS)] : [{ kind: 'value', label, text: formatValue(value) }];
    case 'documentNotes': {
      const notes = documentNotes(evidence);
      return notes.length > 0 ? [{ kind: 'list', label, items: notes }] : [];
    }
    case 'inputs':
      // Each submitted input's state as the check reported it, in the check's own
      // words, shown with the same indicator as everywhere else the input appears.
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        return Object.entries(value).filter(([, entry]) => entry && typeof entry === 'object').map(([k, entry]) => ({
          kind: 'value',
          label: (spec.labels && spec.labels[k]) || humanize(k),
          text: (typeof entry.reason === 'string' && entry.reason.trim()) || humanize(String(entry.status || 'no status')),
          state: {
            kind: inputKind(entry.status),
            enforced: inputEnforced(k, evidence),
            short: INPUT_STATUS[entry.status] ? INPUT_STATUS[entry.status].short : humanize(String(entry.status || 'no status')),
          },
        }));
      }
      return [{ kind: 'value', label, text: formatValue(value) }];
    case 'contradiction':
      if (value && typeof value === 'object' && isNumber(value.score)) {
        const word = (r) => RESULT_WORD[r] || String(r).toLowerCase();
        return [{
          kind: 'value',
          label,
          text: `The score of ${value.score} / 100 gives ${word(value.score_result)}; the model's own verdict was ${word(value.model_result)}.`,
        }];
      }
      return [{ kind: 'value', label, text: JSON.stringify(value) }];
    case 'checkRefs':
      return Array.isArray(value) ? [describeList(label, value, nameOf)] : [{ kind: 'value', label, text: String(value) }];
    case 'parts':
      // Each part by its check's name and result; its full write-up is in the download.
      if (!Array.isArray(value)) return [{ kind: 'value', label, text: formatValue(value) }];
      return [{
        kind: 'list',
        label,
        items: value.filter((p) => p && typeof p === 'object').map((p) => {
          const name = nameOf(String(p.check_id));
          return `${name && name !== String(p.check_id) ? name : humanize(String(p.part || 'a check'))}: ${RESULT_SHOWN[p.result] || humanize(String(p.result || 'no result').toLowerCase())}`;
        }),
      }];
    case 'planDates': {
      if (!value || typeof value !== 'object' || Array.isArray(value)) return [{ kind: 'value', label, text: formatValue(value) }];
      const when = (iso) => (iso ? formatDate(iso) : 'not given');
      const notes = (Array.isArray(value.reasons) ? value.reasons : []).map((r) => r && r.label).filter(Boolean);
      return [
        { kind: 'value', label: 'Request', text: `${when(value.request_start)} to ${when(value.request_end)}` },
        { kind: 'value', label: 'Plan', text: `start ${planSideText(value.plan_start)}; end ${planSideText(value.plan_end)}` },
        ...(value.submitted ? [{ kind: 'value', label: 'Submitted', text: formatDate(value.submitted) }] : []),
        ...(notes.length > 0 ? [{ kind: 'list', label: 'Notes', items: notes }] : []),
      ];
    }
    case 'table':
      if (!Array.isArray(value)) return [{ kind: 'value', label, text: formatValue(value) }];
      return value.length === 0 ? [{ kind: 'value', label, text: 'None' }] : [asTable(label, value, spec.columns)];
    case 'range':
      if (value && isNumber(value.low) && isNumber(value.high)) {
        return [{ kind: 'value', label, text: `${money(value.low)} to ${money(value.high)}` }];
      }
      return [{ kind: 'value', label, text: JSON.stringify(value) }];
    case 'counts':
      if (value && typeof value === 'object') {
        const parts = Object.entries(value).map(([k, n]) => `${(spec.labels && spec.labels[k]) || humanize(k)}: ${n}`);
        return [{ kind: 'value', label, text: parts.length ? parts.join(', ') : 'None' }];
      }
      return [{ kind: 'value', label, text: formatValue(value) }];
    case 'fields':
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        return Object.entries(value).map(([k, v]) => ({
          kind: 'value',
          label: (spec.labels && spec.labels[k]) || humanize(k),
          text: formatValue(v, (spec.formats && spec.formats[k]) || spec.defaultFormat),
        }));
      }
      return [{ kind: 'value', label, text: formatValue(value) }];
    default:
      return [{ kind: 'value', label, text: formatValue(value, format, spec.valueLabels) }];
  }
}

/** A key no spec names, shown under a label made from the key. */
function describeUnspecified(key, value, nameOf) {
  const label = humanize(key);
  if (Array.isArray(value)) return [describeList(label, value)];
  if (value && typeof value === 'object') {
    return Object.entries(value).map(([k, v]) => ({
      kind: 'value', label: `${label}: ${humanize(k)}`, text: typeof v === 'object' ? JSON.stringify(v) : formatValue(v),
    }));
  }
  if (typeof value === 'string' && value.length > 80) return [{ kind: 'paragraph', label, text: value }];
  return [{ kind: 'value', label, text: formatValue(value) }];
}

const criteriaBlock = (spec, evidence) => {
  const status = evidence.plan_quote_status && typeof evidence.plan_quote_status === 'object'
    ? evidence.plan_quote_status : {};
  const recheck = evidence.plan_quote_recheck && typeof evidence.plan_quote_recheck === 'object'
    ? evidence.plan_quote_recheck : {};
  const criteria = spec.criteria
    .filter((c) => evidence[c.key] && typeof evidence[c.key] === 'object')
    .map((c) => {
      const raw = evidence[c.key];
      const again = recheck[c.key] && typeof recheck[c.key] === 'object' ? recheck[c.key] : null;
      const facts = [
        ...Object.entries(c.flags || {})
          .filter(([k]) => typeof raw[k] === 'boolean')
          .map(([k, text]) => ({ label: text, text: raw[k] ? 'Yes' : 'No' })),
        ...Object.entries(c.lists || {})
          .filter(([k]) => Array.isArray(raw[k]))
          .map(([k, text]) => ({ label: text, text: raw[k].length ? raw[k].join(', ') : 'None' })),
      ];
      const known = new Set(['assessment', 'score', 'plan_quote', ...Object.keys(c.flags || {}), ...Object.keys(c.lists || {})]);
      Object.entries(raw).filter(([k]) => !known.has(k)).forEach(([k, v]) => {
        facts.push({ label: humanize(k), text: typeof v === 'object' ? JSON.stringify(v) : formatValue(v) });
      });
      return {
        key: c.key,
        label: c.label,
        score: isNumber(raw.score) ? raw.score : null,
        assessment: typeof raw.assessment === 'string' ? raw.assessment : '',
        // The passage the check returned as its support, and where it was found.
        planQuote: typeof raw.plan_quote === 'string' ? raw.plan_quote.trim() : '',
        planStatus: status[c.key] ? (PLAN_QUOTE_STATUS[status[c.key]] || humanize(status[c.key])) : '',
        // The plan-only second look, when the first passage came from the description.
        recheck: again ? {
          quote: typeof again.plan_quote === 'string' ? again.plan_quote.trim() : '',
          status: again.status ? (PLAN_QUOTE_STATUS[again.status] || humanize(again.status)) : '',
        } : null,
        facts,
      };
    });
  return criteria.length ? [{ kind: 'criteria', label: spec.label, criteria }] : [];
};

/**
 * The evidence keys the review view leaves out (engine switches, routing codes,
 * underscore bookkeeping), for the full-details download, which carries them
 * too. Raw, as stored.
 */
/**
 * Whether a check's details paragraph shows on the page (the download always has it):
 * yes, unless its spec leaves it off because another field says the same, and that
 * field is on this record with something to render.
 */
export const showsDetails = (checkId, evidence) => {
  const spec = EVIDENCE_SPECS[checkId];
  if (!spec || spec.detailsOnPage !== false) return true;
  const replacement = evidence && spec.detailsReplacedBy ? evidence[spec.detailsReplacedBy] : null;
  const renders = replacement && typeof replacement === 'object' && !Array.isArray(replacement)
    && Object.values(replacement).some((entry) => entry && typeof entry === 'object');
  return !renders;
};

export function internalEvidence(checkId, evidence) {
  if (!evidence || typeof evidence !== 'object' || Array.isArray(evidence)) return [];
  const spec = EVIDENCE_SPECS[checkId] || { fields: [] };
  const internal = new Set([...SHARED_INTERNAL, ...(spec.internal || [])]);
  internal.delete('evidence_quotes');
  return Object.entries(evidence)
    .filter(([key, value]) => (internal.has(key) || key.startsWith('_')) && value !== undefined && value !== null && value !== '')
    .map(([key, value]) => ({ key, value }));
}

/**
 * A check's evidence as an ordered list of display items:
 *   {kind: 'value', label, text}        a labelled value
 *   {kind: 'list', label, items}        a labelled list
 *   {kind: 'paragraph', label, text}    a labelled block of prose
 *   {kind: 'table', label, columns, rows}
 *   {kind: 'criteria', label, criteria} scored criteria, each with its assessment
 *                                       and the plan passage that supports it
 *
 * @param {string} checkId  the agent's check id (internal, never displayed)
 * @param {object} evidence the check's stored evidence
 * @param {string} details  the check's one-line details, so a deferral reason
 *                          that only repeats them is not shown twice
 * @param {(id: string) => string} nameOf  a check name for a check id
 * @param {{omitReason?: boolean}} options  omitReason leaves out why the check could
 *                          not run, for a view that already shows it above the check
 */
export function describeEvidence(checkId, evidence, details = '', nameOf = (id) => id, options = {}) {
  if (!evidence || typeof evidence !== 'object' || Array.isArray(evidence)) return [];
  const spec = EVIDENCE_SPECS[checkId] || { fields: [] };
  const internal = new Set([...SHARED_INTERNAL, ...(spec.internal || [])]);
  const named = new Set();
  const items = [];

  spec.fields.forEach((field) => {
    if (field.format === 'criteria') {
      field.criteria.forEach((c) => named.add(c.key));
      items.push(...criteriaBlock(field, evidence));
      return;
    }
    named.add(field.key);
    // `pick` reads one key of each object in a list (a service's name), the first of
    // several keys that is present (a file's plain name before its stored one).
    const raw = evidence[field.key];
    const pickFrom = (item) => {
      const key = [].concat(field.pick).find((k) => item[k] !== undefined && item[k] !== null);
      return key ? item[key] : undefined;
    };
    const value = field.pick && Array.isArray(raw)
      ? raw.map((item) => (item && typeof item === 'object' ? pickFrom(item) : item)) : raw;
    if (value === undefined || value === null || value === '') return;
    if (field.onlyIfTrue && value !== true) return;
    // A reasoning check's details carry its assessment (and any note to verify),
    // already on the row, so a paragraph the details contain is not repeated.
    if (field.format === 'paragraph' && typeof value === 'string' && details
        && details.includes(value.trim())) return;
    // Likewise a list the agent also writes into the details, once the details
    // carry every item of it.
    if (field.inDetails && Array.isArray(value) && details
        && value.every((item) => typeof item === 'string' && details.includes(item.trim()))) return;
    items.push(...describeField(field, value, nameOf, evidence));
  });

  Object.entries(evidence).forEach(([key, value]) => {
    if (named.has(key) || internal.has(key) || key.startsWith('_')) return;
    if (value === undefined || value === null || value === '') return;
    if (key === 'reason' && typeof value === 'string') {
      if (options.omitReason) return;
      const plain = value.trim();
      if (details && details.includes(plain)) return;
      items.push({ kind: 'paragraph', label: 'Why it could not run', text: plain });
      return;
    }
    items.push(...describeUnspecified(key, value, nameOf));
  });
  return items;
}

/**
 * A check's details as the review page shows them. The agent's internal tier code
 * ("Tier: ISFR_STANDARD.") is left out wherever the check recorded its tier (the
 * download keeps it as engine detail), and a spec's `line` adds what the reviewer
 * needs beside it.
 */
export function displayDetails(checkId, details, evidence = {}) {
  let shown = typeof details === 'string' ? details : '';
  if (typeof evidence.tier === 'string' && evidence.tier) {
    shown = shown.replace(`Tier: ${evidence.tier}. `, '').replace(`Tier: ${evidence.tier}.`, '').trim();
  }
  const spec = EVIDENCE_SPECS[checkId];
  return spec && spec.line && shown ? spec.line(evidence, shown) : shown;
}
